import { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { getHomePdf } from '../utils/api';

const FOUNDATION_OPTS = [
  { value: 'slab', label: 'Slab' },
  { value: 'crawl-space', label: 'Crawl Space' },
  { value: 'basement', label: 'Full Basement' },
  { value: 'basement-crawl-space', label: 'Basement + Crawl Space' },
];

const EXTERIOR_OPTS = [
  { value: 'brick', label: 'Brick' },
  { value: 'vinyl-siding', label: 'Vinyl Siding' },
  { value: 'wood', label: 'Wood' },
  { value: 'stucco', label: 'Stucco' },
  { value: 'fiber-cement', label: 'Fiber Cement (Hardie)' },
  { value: 'stone', label: 'Stone / Veneer' },
  { value: 'other', label: 'Other' },
];

const ROOF_OPTS = [
  { value: 'asphalt-shingle', label: 'Asphalt Shingle' },
  { value: 'metal', label: 'Metal' },
  { value: 'tile', label: 'Tile' },
  { value: 'flat-membrane', label: 'Flat / Membrane' },
  { value: 'wood-shake', label: 'Wood Shake' },
  { value: 'other', label: 'Other' },
];

const GARAGE_OPTS = [
  { value: 'none', label: 'None' },
  { value: 'attached-1-car', label: 'Attached 1-Car' },
  { value: 'attached-2-car', label: 'Attached 2-Car' },
  { value: 'attached-3-car', label: 'Attached 3-Car' },
  { value: 'detached', label: 'Detached' },
];

const HEATING_OPTS = [
  { value: 'forced-air-gas', label: 'Forced Air — Gas' },
  { value: 'forced-air-electric', label: 'Forced Air — Electric' },
  { value: 'heat-pump', label: 'Heat Pump' },
  { value: 'boiler-radiator', label: 'Boiler / Radiator' },
  { value: 'radiant-floor', label: 'Radiant Floor' },
  { value: 'baseboard-electric', label: 'Baseboard Electric' },
  { value: 'other', label: 'Other' },
];

const COOLING_OPTS = [
  { value: 'central-ac', label: 'Central AC' },
  { value: 'heat-pump', label: 'Heat Pump' },
  { value: 'mini-splits', label: 'Mini-Splits' },
  { value: 'window-units', label: 'Window Units' },
  { value: 'none', label: 'None' },
];

const WATER_HEATER_OPTS = [
  { value: 'tank-gas', label: 'Tank — Gas' },
  { value: 'tank-electric', label: 'Tank — Electric' },
  { value: 'tankless-gas', label: 'Tankless — Gas' },
  { value: 'tankless-electric', label: 'Tankless — Electric' },
  { value: 'heat-pump-water-heater', label: 'Heat Pump Water Heater' },
  { value: 'other', label: 'Other' },
];

const PANEL_OPTS = [
  { value: '60a', label: '60A' },
  { value: '100a', label: '100A' },
  { value: '150a', label: '150A' },
  { value: '200a', label: '200A' },
  { value: '400a', label: '400A' },
  { value: 'other', label: 'Other / Unknown' },
];

const FIREPLACE_OPTS = [
  { value: 'none', label: 'None' },
  { value: 'gas', label: 'Gas' },
  { value: 'wood-burning', label: 'Wood Burning' },
  { value: 'electric', label: 'Electric' },
];

const BLANK = {
  yearBuilt: '', squareFootage: '', floors: '', bedrooms: '', bathrooms: '', lotSizeSqft: '',
  city: '', state: '', zipCode: '',
  foundationType: '', exteriorMaterial: '', roofMaterial: '', roofYear: '', garageType: '',
  heatingType: '', coolingType: '', waterSource: '', sewerType: '', electricalPanel: '',
  waterHeaterType: '', waterHeaterYear: '',
  hasPool: false, hasSpa: false, hasGenerator: false, hasSolarPanels: false,
  fireplaceCount: '', fireplaceType: '',
  notes: '',
};

const SelectField = ({ label, name, value, onChange, opts, placeholder = 'Select…' }) => (
  <div className="form-group">
    <label>{label}</label>
    <select name={name} value={value} onChange={onChange}>
      <option value="">{placeholder}</option>
      {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const NumField = ({ label, name, value, onChange, placeholder }) => (
  <div className="form-group">
    <label>{label}</label>
    <input type="number" name={name} value={value} onChange={onChange} placeholder={placeholder} />
  </div>
);

const CheckField = ({ label, name, checked, onChange }) => (
  <label className="checkbox-label">
    <input type="checkbox" name={name} checked={checked} onChange={onChange} />
    {label}
  </label>
);

export default function HomeProfile() {
  const { home, fetchHome, saveHome, setError } = useApp();
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchHome();
  }, [fetchHome]);

  useEffect(() => {
    if (home && home._id) {
      setForm({
        ...BLANK,
        ...Object.fromEntries(
          Object.entries(home).map(([k, v]) => [k, v === null || v === undefined ? '' : v])
        ),
      });
    }
  }, [home]);

  const handle = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
    setSaved(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await saveHome(form);
      setSaved(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save home profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Home Profile</h1>
        <p className="page-subtitle">Basic details about your home — used to personalise AI maintenance suggestions.</p>
      </div>

      <form onSubmit={handleSubmit} className="home-profile-form">

        {/* Basic Info */}
        <section className="profile-section">
          <h2>Basic Info</h2>
          <div className="profile-grid">
            <NumField label="Year Built" name="yearBuilt" value={form.yearBuilt} onChange={handle} placeholder="1952" />
            <NumField label="Square Footage" name="squareFootage" value={form.squareFootage} onChange={handle} placeholder="1800" />
            <NumField label="Floors" name="floors" value={form.floors} onChange={handle} placeholder="2" />
            <NumField label="Bedrooms" name="bedrooms" value={form.bedrooms} onChange={handle} placeholder="3" />
            <NumField label="Bathrooms" name="bathrooms" value={form.bathrooms} onChange={handle} placeholder="2.5" />
            <NumField label="Lot Size (sq ft)" name="lotSizeSqft" value={form.lotSizeSqft} onChange={handle} placeholder="8000" />
          </div>
        </section>

        {/* Location */}
        <section className="profile-section">
          <h2>Location</h2>
          <div className="profile-grid">
            <div className="form-group">
              <label>City</label>
              <input type="text" name="city" value={form.city} onChange={handle} placeholder="Springfield" />
            </div>
            <div className="form-group">
              <label>State</label>
              <input type="text" name="state" value={form.state} onChange={handle} placeholder="IL" maxLength={2} />
            </div>
            <div className="form-group">
              <label>ZIP Code</label>
              <input type="text" name="zipCode" value={form.zipCode} onChange={handle} placeholder="62701" maxLength={10} />
            </div>
          </div>
        </section>

        {/* Structure */}
        <section className="profile-section">
          <h2>Structure</h2>
          <div className="profile-grid">
            <SelectField label="Foundation" name="foundationType" value={form.foundationType} onChange={handle} opts={FOUNDATION_OPTS} />
            <SelectField label="Exterior Material" name="exteriorMaterial" value={form.exteriorMaterial} onChange={handle} opts={EXTERIOR_OPTS} />
            <SelectField label="Roof Material" name="roofMaterial" value={form.roofMaterial} onChange={handle} opts={ROOF_OPTS} />
            <NumField label="Roof Last Replaced (year)" name="roofYear" value={form.roofYear} onChange={handle} placeholder="2015" />
            <SelectField label="Garage" name="garageType" value={form.garageType} onChange={handle} opts={GARAGE_OPTS} />
          </div>
        </section>

        {/* HVAC & Utilities */}
        <section className="profile-section">
          <h2>HVAC &amp; Utilities</h2>
          <div className="profile-grid">
            <SelectField label="Heating System" name="heatingType" value={form.heatingType} onChange={handle} opts={HEATING_OPTS} />
            <SelectField label="Cooling System" name="coolingType" value={form.coolingType} onChange={handle} opts={COOLING_OPTS} />
            <SelectField label="Water Source" name="waterSource" value={form.waterSource} onChange={handle} opts={[{ value: 'municipal', label: 'Municipal / City Water' }, { value: 'well', label: 'Private Well' }]} />
            <SelectField label="Sewer" name="sewerType" value={form.sewerType} onChange={handle} opts={[{ value: 'municipal', label: 'Municipal Sewer' }, { value: 'septic', label: 'Septic System' }]} />
            <SelectField label="Electrical Panel" name="electricalPanel" value={form.electricalPanel} onChange={handle} opts={PANEL_OPTS} />
          </div>
        </section>

        {/* Water Heater */}
        <section className="profile-section">
          <h2>Water Heater</h2>
          <div className="profile-grid">
            <SelectField label="Type" name="waterHeaterType" value={form.waterHeaterType} onChange={handle} opts={WATER_HEATER_OPTS} />
            <NumField label="Year Installed" name="waterHeaterYear" value={form.waterHeaterYear} onChange={handle} placeholder="2018" />
          </div>
        </section>

        {/* Features */}
        <section className="profile-section">
          <h2>Features</h2>
          <div className="profile-grid">
            <SelectField label="Fireplace Type" name="fireplaceType" value={form.fireplaceType} onChange={handle} opts={FIREPLACE_OPTS} />
            <NumField label="Fireplace Count" name="fireplaceCount" value={form.fireplaceCount} onChange={handle} placeholder="1" />
          </div>
          <div className="checkbox-group">
            <CheckField label="In-ground Pool" name="hasPool" checked={form.hasPool} onChange={handle} />
            <CheckField label="Hot Tub / Spa" name="hasSpa" checked={form.hasSpa} onChange={handle} />
            <CheckField label="Backup Generator" name="hasGenerator" checked={form.hasGenerator} onChange={handle} />
            <CheckField label="Solar Panels" name="hasSolarPanels" checked={form.hasSolarPanels} onChange={handle} />
          </div>
        </section>

        {/* Notes */}
        <section className="profile-section">
          <h2>Notes</h2>
          <div className="form-group">
            <textarea
              name="notes"
              value={form.notes}
              onChange={handle}
              rows={4}
              placeholder="Any other details about the home that would help with maintenance planning…"
            />
          </div>
        </section>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save Profile'}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={exporting}
            onClick={async () => {
              setExporting(true);
              try {
                const blob = await getHomePdf();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'home-report.pdf';
                a.click();
                URL.revokeObjectURL(url);
              } finally {
                setExporting(false);
              }
            }}
          >
            {exporting ? 'Generating…' : '↓ Export Home Report'}
          </button>
          {saved && <span className="save-confirm">Saved ✓</span>}
        </div>
      </form>
    </div>
  );
}
